/**
 * Privacy gate for visitor geo / VPN / Tor.
 *
 * Edge snapshot (`__TEMPLATE_CONFIG__.privacyFlags`) is the source of truth.
 * SPA layers a bypass for operators:
 *   - super admin (Clerk)
 *   - geo_sim + Clean (mapa del país sin privacy lock)
 *
 * VPN sim (simulate_vpn=anonymous|hosting) keeps the lock for QA.
 */

import type { ServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { readGeoSimSession } from "./session";
import type { GeoSimVpnMode } from "./constants";

export type PrivacyFlags = NonNullable<ServerTemplateConfig["privacyFlags"]> & {
  is_threat?: boolean;
  is_anonymous?: boolean;
  is_bogon?: boolean;
};

/** Residential Clean allowlist (operator real traffic) — mirrors Worker. */
export const GEO_PRIVACY_CLEAN_IP_ALLOWLIST = ["190.229.184.199"] as const;

/** Tor exit example for VPN-sim QA — must NOT apply in Clean. */
export const GEO_SIM_EXAMPLE_TOR_IP = "185.220.101.1";

/** Residential Clean example for simulate_ip in Clean mode. */
export const GEO_SIM_EXAMPLE_CLEAN_IP = "190.229.184.199";

export function isPrivacyLockSignal(
  flags: PrivacyFlags | null | undefined,
  vpnDetected?: boolean,
  isHosting?: boolean,
): boolean {
  if (vpnDetected === true || isHosting === true) return true;
  if (!flags || typeof flags !== "object") return false;
  return !!(
    flags.is_vpn ||
    flags.is_proxy ||
    flags.is_tor ||
    flags.is_relay ||
    flags.is_anonymous ||
    flags.is_abuser ||
    flags.is_bogon ||
    flags.is_threat ||
    flags.is_hosting
  );
}

function simulateVpnModeFromConfig(
  adminGeoSim: ServerTemplateConfig["adminGeoSim"] | null | undefined,
  sessionMode?: GeoSimVpnMode | null,
): GeoSimVpnMode | null {
  if (sessionMode === "clean" || sessionMode === "anonymous" || sessionMode === "hosting") {
    return sessionMode;
  }
  if (!adminGeoSim) return null;
  if (typeof adminGeoSim.simulateVpn === "string") {
    const m = adminGeoSim.simulateVpn.toLowerCase();
    if (m === "clean" || m === "anonymous" || m === "hosting") return m;
  }
  if (adminGeoSim.vpn) return "anonymous";
  if (adminGeoSim.hosting) return "hosting";
  return "clean";
}

/** sessionStorage: set by GeoSimToolbar when operator is super admin / QA */
export const PRIVACY_GATE_BYPASS_KEY = "admin_privacy_gate_bypass";

export function markPrivacyGateBypass(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(PRIVACY_GATE_BYPASS_KEY, "1");
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(PRIVACY_GATE_BYPASS_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function isPrivacyGateBypassMarked(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (localStorage.getItem(PRIVACY_GATE_BYPASS_KEY) === "1") return true;
  } catch {
    /* ignore */
  }
  try {
    return sessionStorage.getItem(PRIVACY_GATE_BYPASS_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Bypass privacy lock (SPA):
 * 1. Super admin / QA (session flag set when Clerk confirms role)
 * 2. Active geo_sim with Clean mode
 *
 * VPN sim (anonymous/hosting) does NOT bypass — gate stays on for QA.
 */
export function shouldBypassPrivacyGate(opts: {
  isSuperAdmin?: boolean;
  adminGeoSim?: ServerTemplateConfig["adminGeoSim"] | null;
  /** sessionStorage sim (optional; read if omitted) */
  sessionSimulateVpn?: GeoSimVpnMode | null;
  /** force read sessionStorage */
  readSession?: boolean;
}): boolean {
  // If operator is mid VPN-sim, never bypass (QA must see the lock).
  let sessionMode = opts.sessionSimulateVpn ?? null;
  if (opts.readSession !== false && sessionMode == null) {
    const s = readGeoSimSession();
    sessionMode = s?.simulateVpn ?? null;
  }
  const mode = simulateVpnModeFromConfig(opts.adminGeoSim, sessionMode);
  if (mode === "anonymous" || mode === "hosting") {
    return false;
  }

  if (opts.isSuperAdmin === true) return true;
  if (isPrivacyGateBypassMarked()) return true;

  if (mode === "clean") return true;

  // URL intent: geo_sim=1 without VPN force
  if (typeof window !== "undefined") {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("geo_sim") === "1") {
        const vpn = (p.get("simulate_vpn") || "clean").toLowerCase();
        if (vpn === "clean" || vpn === "" || vpn === "none") {
          if (!opts.adminGeoSim?.vpn && !opts.adminGeoSim?.hosting) return true;
        }
      }
    } catch {
      /* ignore */
    }
  }

  return false;
}

/** Enforce privacy lock only when signal is present AND not bypassed. */
export function shouldEnforcePrivacyLock(opts: {
  isSuperAdmin?: boolean;
  adminGeoSim?: ServerTemplateConfig["adminGeoSim"] | null;
  privacyFlags?: PrivacyFlags | null;
  vpnDetected?: boolean;
  isHosting?: boolean;
  sessionSimulateVpn?: GeoSimVpnMode | null;
}): boolean {
  if (
    shouldBypassPrivacyGate({
      isSuperAdmin: opts.isSuperAdmin,
      adminGeoSim: opts.adminGeoSim,
      sessionSimulateVpn: opts.sessionSimulateVpn,
    })
  ) {
    return false;
  }
  return isPrivacyLockSignal(
    opts.privacyFlags,
    opts.vpnDetected,
    opts.isHosting,
  );
}

/**
 * Sanitize privacy for SPA consumers when bypass applies
 * (defensive second layer if edge/snapshot still has Tor flags).
 */
export function sanitizePrivacyForClient(opts: {
  isSuperAdmin?: boolean;
  adminGeoSim?: ServerTemplateConfig["adminGeoSim"] | null;
  privacyFlags?: PrivacyFlags | null;
  vpnDetected?: boolean;
  isHosting?: boolean;
}): {
  privacyFlags: PrivacyFlags | null;
  vpnDetected: boolean;
  isHosting: boolean;
  privacyLockActive: boolean;
  privacyBypassed: boolean;
} {
  const bypass = shouldBypassPrivacyGate({
    isSuperAdmin: opts.isSuperAdmin,
    adminGeoSim: opts.adminGeoSim,
  });
  if (bypass) {
    return {
      privacyFlags: null,
      vpnDetected: false,
      isHosting: false,
      privacyLockActive: false,
      privacyBypassed: true,
    };
  }
  const lock = isPrivacyLockSignal(
    opts.privacyFlags,
    opts.vpnDetected,
    opts.isHosting,
  );
  return {
    privacyFlags: opts.privacyFlags ?? null,
    vpnDetected: opts.vpnDetected === true,
    isHosting: opts.isHosting === true,
    privacyLockActive: lock,
    privacyBypassed: false,
  };
}
